'use client';

import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, Link } from "@mui/material";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import DesignsSlideshowComponent, { DesignerSlideShowData } from "@/app/components/designs_slideshow";
import { Slide } from "@/app/components/designs_slideshow";
import FavoriteButton from "@/app/components/FavoriteButton";
import FavoritesProvider from "./FavoritesProvider";
import { usePathname } from "next/navigation";
import { DeviceDetector, gtmClickTracking } from "@/utils";
import cssStyles from '../../[vendor]/product/product.module.scss';

interface AlternateImageData {
    img: string;
    rows?: number;
    cols?: number;
}

interface ProductImageGalleryProps {
    productId: string;
    productName: string;
    productImage: string;
    additionalImageUrls?: string[];
    model3dUrl?: string;
    price?: number;
    comparePrice?: number;
    isBestSeller?: boolean;
    viewInYourRoomLink: string;
    popupView?: boolean;
}

/**
 * Client island for product image gallery with slideshow, 3D toggle, and favorite button.
 */
const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
    productId,
    productName,
    productImage,
    additionalImageUrls,
    model3dUrl,
    price,
    comparePrice,
    isBestSeller,
    viewInYourRoomLink,
    popupView,
}) => {
    const pathname = usePathname();
    const device = DeviceDetector();
    const [show3d, setShow3d] = useState(false);
    const [productAlternateImage, setProductAlternateImage] = useState<AlternateImageData[]>([]);

    useEffect(() => {
        if (!popupView && additionalImageUrls && additionalImageUrls.length > 0) {
            const uniqueUrls = additionalImageUrls.filter((item, index) => additionalImageUrls.indexOf(item) === index);
            const altImages = uniqueUrls.map((altImg, index) => {
                const cols = (index > 0 && (index + 1) % 3 === 0) ? 2 : 1;
                return { img: altImg, cols, rows: 1 };
            });
            setProductAlternateImage(altImages);
        }
    }, [additionalImageUrls, popupView]);

    const toggle3d = () => {
        setShow3d(!show3d);
        gtmClickTracking({
            event: '3d_button_clicked',
            page: pathname,
            ecommerce: {
                item_id: productId,
                item_name: productName,
                price,
            },
        });
    };

    const prepareDesignerSlideShowData = (images: AlternateImageData[]): DesignerSlideShowData => {
        const slides: Slide[] = images.map((image, index) => ({
            id: "design" + index,
            image: image.img,
        } as Slide));
        const carouselSlides = [{ id: "product-image", image: productImage }, ...slides];
        return { designs: carouselSlides, dispatcher: {} };
    };

    const threeDPlaceholder = (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div style={{ width: '100%', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
                <Typography>3D View</Typography>
            </div>
            <div className={cssStyles.bottomCenterText}>
                <Link href={viewInYourRoomLink} style={{ textDecoration: 'none' }}>
                    <Typography
                        sx={{
                            margin: '0px', fontSize: '1.4rem', fontWeight: 700,
                            color: 'primary_extent.mainDark',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        textAlign="center" gutterBottom variant="h5" component="div"
                    >
                        View this product in your room
                    </Typography>
                </Link>
            </div>
        </div>
    );

    const button3d = model3dUrl && (
        <div className={cssStyles["button-3d"]} onClick={() => toggle3d()}>
            <ViewInArIcon />
            {show3d ? 'VIEW IN 2D' : 'VIEW IN 3D'}
        </div>
    );

    const bestSellerBadge = isBestSeller && (
        <div className={cssStyles["bestSeller-icon"]} style={{
            position: 'absolute', top: device === 'Mobile' ? 140 : 10, left: 10, zIndex: 100, pointerEvents: 'none',
        }}>
            <img src={device === 'Mobile' ? "/assets/img/bestseller-icon@1x.png" : "/assets/img/bestseller-icon@1.25x.png"} alt="Best Seller" />
        </div>
    );

    return (
        <FavoritesProvider>
            {({ userInfo, favorites, setFavorites }) => (
                <>
                    {device === 'Mobile' ? (
                        <>
                            {show3d ? (model3dUrl && threeDPlaceholder) : (
                                <DesignsSlideshowComponent {...prepareDesignerSlideShowData(productAlternateImage)} />
                            )}
                            {bestSellerBadge}
                            <div style={{ position: 'absolute', top: 140, right: 10, zIndex: 9999 }}>
                                <FavoriteButton
                                    customerId={userInfo?.cId}
                                    productId={productId}
                                    productName={productName}
                                    favorites={favorites}
                                    price={price}
                                    comparePrice={comparePrice}
                                    imageUrl={productImage}
                                    model3dUrl={model3dUrl}
                                    setFavorites={setFavorites}
                                />
                            </div>
                            {button3d}
                        </>
                    ) : (
                        <Box component="div" id="left-section-wrapper">
                            <Box component="div" className={cssStyles["product-image__section"]} sx={{ borderColor: 'secondary.main' }}>
                                {productImage && (
                                    <Paper variant="outlined" className={cssStyles["product-image__section--wrapper"]} style={{ position: 'relative' }}>
                                        {bestSellerBadge}
                                        <FavoriteButton
                                            customerId={userInfo?.cId}
                                            productId={productId}
                                            productName={productName}
                                            price={price}
                                            comparePrice={comparePrice}
                                            imageUrl={productImage}
                                            model3dUrl={model3dUrl}
                                            favorites={favorites}
                                            setFavorites={setFavorites}
                                        />
                                        {show3d ? (model3dUrl ? threeDPlaceholder : null) : (
                                            productImage ? (
                                                <Box component="div" sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'center' }}>
                                                    <DesignsSlideshowComponent {...prepareDesignerSlideShowData(productAlternateImage)} />
                                                </Box>
                                            ) : null
                                        )}
                                        {button3d}
                                    </Paper>
                                )}
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </FavoritesProvider>
    );
};

export default ProductImageGallery;
